import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Switch — binary on/off toggle.
 *
 * Tailwind-only (no Radix dependency). Accepts the same `checked` /
 * `onChange(next: boolean)` shape as `<Checkbox>`. Use for activating /
 * deactivating something, especially in lists.
 *
 * Sizes: `sm` (h-[1.6rem]) · `md` (default, h-[2rem]) · `lg` (h-[2.4rem]).
 *
 * @example
 *   <Switch checked={active} onChange={setActive} />
 */
const SIZES = {
  sm: {
    track: "h-[1.6rem] w-[3rem]",
    thumb: "h-[1.2rem] w-[1.2rem]",
    on: "translate-x-[1.4rem]",
  },
  md: {
    track: "h-[2rem] w-[3.6rem]",
    thumb: "h-[1.6rem] w-[1.6rem]",
    on: "translate-x-[1.6rem]",
  },
  lg: {
    track: "h-[2.4rem] w-[4.4rem]",
    thumb: "h-[2rem] w-[2rem]",
    on: "translate-x-[2rem]",
  },
};

export const Switch = React.forwardRef(
  ({ checked = false, onChange, disabled, size = "md", className, ...props }, ref) => {
    const s = SIZES[size] || SIZES.md;
    const handleClick = (e) => {
      if (disabled) return;
      e.preventDefault();
      onChange?.(!checked);
    };
    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={handleClick}
        className={cn(
          "relative inline-flex shrink-0 cursor-pointer rounded-full p-[0.2rem]",
          "transition-colors duration-150 outline-none border-none",
          "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          s.track,
          checked ? "bg-primary" : "bg-[#d4d4d8]",
          className,
        )}
        {...props}
      >
        <span
          className={cn(
            "inline-block rounded-full bg-white shadow-[0_0.1rem_0.2rem_rgba(0,0,0,0.2)]",
            "transform transition-transform duration-150",
            s.thumb,
            checked ? s.on : "translate-x-0",
          )}
        />
      </button>
    );
  },
);
Switch.displayName = "Switch";
